import { GLContext } from "../rendering/gl_context.js";
import { EmitterBatch } from "../batching/emitter_batch.js";
import { InstanceBatch } from "../batching/instance_batch.js";
import { IRenderLayerStats } from "../types/webgl_types.js";

/**
 * FxRenderLayer — оркестратор одного WebGL-контекста.
 * Эффекты регистрируются через `register()` один раз при инициализации.
 */
export class FxRenderLayer
{
  public readonly canvas      : HTMLCanvasElement;
  public readonly gl_ctx      : GLContext;
  private readonly effects    : InstanceBatch[] = [];
  private readonly effect_map : Map<Function, InstanceBatch> = new Map();
  private last_update_time    : number = 0;

  constructor( canvas: HTMLCanvasElement )
  {
    this.canvas = canvas;

    const gl = canvas.getContext( "webgl2", {
      alpha: true,
      antialias: false,
      depth: false,
    } );
    if ( !gl )
    {
      throw new Error(
        "Failed to initialize WebGL2 context for render layer. " +
        "Your browser or device may not support WebGL2."
      );
    }

    this.gl_ctx = new GLContext( gl );
  }

  /** Регистрирует batch-эффект. Вызывается один раз на тип при инициализации. Возвращает эффект для удобства. */
  public register<T extends InstanceBatch>( effect: T ): T
  {
    effect.init( this.gl_ctx.gl );
    this.effects.push( effect );
    this.effect_map.set( effect.constructor, effect );
    return effect;
  }

  /** Возвращает зарегистрированный эффект по конструктору класса — O(1) */
  public get<T extends InstanceBatch>( ctor: new ( ...args: any[] ) => T ): T
  {
    const found = this.effect_map.get( ctor );
    if ( !found )
      throw new Error( `Effect ${ctor.name} is not registered` );
    return found as T;
  }

  /** Повертає час останнього updateGroup/update виклику. */
  public getLastUpdateTime(): number { return this.last_update_time; }

  /** Обновляет буферы всех зарегистрированных эффектов */
  public update( now: number ): void
  {
    this.last_update_time = now;
    const gl = this.gl_ctx.gl;
    for ( const e of this.effects )
      e.updateBuffer( now, gl );
  }

  /** Рисует все активные эффекты (полный цикл: clear → render → restore). */
  public render( dpr: number ): void
  {
    const gl = this.gl_ctx.gl;
    this.gl_ctx.updateGlobalParams( dpr, this.last_update_time );
    this.gl_ctx.clear();

    for ( const e of this.effects )
      e.render( gl );

    this.gl_ctx.restoreDefaults();
  }

  // The GL viewport is set once at context creation and does not follow a
  // later change of the canvas drawing-buffer size, unlike the projection
  // (recomputed each frame in updateGlobalParams).
  public handleCanvasResize(): void
  {
    this.gl_ctx.gl.viewport( 0, 0, this.canvas.width, this.canvas.height );
  }

  /** Есть ли активные эффекты? */
  public hasActiveEffects(): boolean
  {
    for ( const e of this.effects )
      if ( e.hasInstances() || ( e instanceof EmitterBatch && e.hasActiveEmitters() ) )
        return true;
    return false;
  }

  public getDetailedStats(): Record<string, { active: number; pending: number; emitters: number }>
  {
    const out: Record<string, { active: number; pending: number; emitters: number }> = {};
    for ( const e of this.effects )
    {
      const active = e.getActiveCount();
      const pending = e.getPendingCount();
      const emitters = e instanceof EmitterBatch ? e.getEmitterCount() : 0;
      if ( active > 0 || pending > 0 || emitters > 0 )
        out[e.constructor.name] = { active, pending, emitters };
    }
    return out;
  }

  public getStats(): IRenderLayerStats
  {
    let active = 0;
    let pending = 0;
    for ( const e of this.effects )
    {
      active += e.getActiveCount();
      pending += e.getPendingCount();
    }
    return { active, pending };
  }

  /** Убивает все частицы во всех эффектах */
  public clearAll(): void
  {
    for ( const e of this.effects )
      e.killAll();
  }

  public warmup(): void
  {
    this.gl_ctx.updateGlobalParams( 1.0, 0 );
    const gl = this.gl_ctx.gl;
    for ( const e of this.effects )
      e.warmup( gl );
  }

  /** Освобождает все GPU-ресурсы и очищает массивы */
  public dispose(): void
  {
    const gl = this.gl_ctx.gl;
    for ( const e of this.effects )
    {
      e.killAll();
      e.dispose( gl );
    }

    this.effects.length = 0;
    this.effect_map.clear();
    this.gl_ctx.dispose();
  }
}
